import React, { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { Dialog } from 'components/common';
import { ActionField } from 'components/fields';
import formatPrice from 'services/helpers/formatPrice';
import { DeferredCreditRule, DeliveryService } from '../types';
import DeleteCampaignActionField from './DeleteCampaingActionField';
import ExportCampaignActionField from './ExportCampaignActionField';

const useStyles = makeStyles((theme) => ({
  content: {
    minWidth: 0,
  },

  title: {
    paddingRight: theme.spacing(8),
  },

  campaign: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
  },

  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1,
    minWidth: 0,
  },

  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    minHeight: 24,
  },

  label: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    flex: 1,
  },

  value: {
    fontSize: 13,
    textAlign: 'right',
    wordBreak: 'break-word',
    flex: 1,
    minWidth: 0,
  },

  divider: {
    margin: theme.spacing(1.5, 0),
  },

  servicesTitle: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },

  services: {
    maxHeight: 320,
    overflow: 'auto',
  },

  table: {
    tableLayout: 'fixed',
    minWidth: 460,
  },

  cell: {
    fontSize: 12,
    padding: theme.spacing(0.75, 1),
    wordBreak: 'break-word',
  },

  hint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

export interface DeferredCreditCampaignRecord {
  id: string;
  name?: string;
  details?: {
    currency?: string;
    deferred?:
      | (Pick<DeferredCreditRule, 'spendThreshold' | 'earnWindowDays'> &
          Partial<DeferredCreditRule>)
      | null;
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  record: DeferredCreditCampaignRecord;
}

interface DeliveryServicesState {
  items: DeliveryService[];
  status: 'loading' | 'loaded' | 'error';
  error?: string;
}

const booleanLabel = (value?: boolean) =>
  value === true ? 'Yes' : value === false ? 'No' : 'Not specified';

const DeferredCreditDetailsDialog = ({ open, onClose, record }: Props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const { deferred, currency = 'USD' } = record.details || {};
  const excludedServices = deferred?.excludedServices;
  const hasExcludedServices = Array.isArray(excludedServices) && excludedServices.length > 0;
  const [deliveryServicesState, setDeliveryServicesState] = useState<DeliveryServicesState>({
    items: [],
    status: 'loading',
  });
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!open || !hasExcludedServices) {
      return;
    }

    let active = true;
    setDeliveryServicesState({ items: [], status: 'loading' });

    const fetchDeliveryServices = async () => {
      try {
        const { data } = await dataProvider.get('deliveryServices');

        if (!active) {
          return;
        }

        if (
          !Array.isArray(data) ||
          !data.every(
            (service) =>
              service &&
              typeof service.provider === 'string' &&
              !!service.provider.trim() &&
              typeof service.code === 'string' &&
              !!service.code.trim()
          )
        ) {
          throw new Error('Invalid carrier services response');
        }

        setDeliveryServicesState({ items: data, status: 'loaded' });
      } catch (error) {
        if (active) {
          setDeliveryServicesState({
            items: [],
            status: 'error',
            error: error?.message || 'Failed to load carrier services',
          });
        }
      }
    };

    fetchDeliveryServices();

    return () => {
      active = false;
    };
  }, [dataProvider, open, record.id, hasExcludedServices, retry]);

  const serviceNames = new Map<string, string>();
  deliveryServicesState.items.forEach((service) => {
    const key = `${service.provider}|${service.code}`;
    const label = typeof service.label === 'string' ? service.label.trim() : '';

    if (label && !serviceNames.has(key)) {
      serviceNames.set(key, label);
    }
  });

  const renderRow = (label: string, value: string) => (
    <Box className={classes.row}>
      <Typography className={classes.label}>{label}</Typography>
      <Typography className={classes.value}>{value}</Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      title="Deferred credit details"
      onCancel={onClose}
      cancelText="Close"
      maxWidth="md"
      fullWidth
      classes={{ content: classes.content, title: classes.title }}>
      <Box className={classes.campaign}>
        <Box className={classes.rows}>
          {renderRow('Campaign name', record.name || 'Not specified')}
          {renderRow('Campaign ID', record.id)}
        </Box>
        <ActionField record={record}>
          <ExportCampaignActionField />
          <DeleteCampaignActionField onDeleted={onClose} />
        </ActionField>
      </Box>

      <Divider className={classes.divider} />

      <Box className={classes.rows}>
        {renderRow(
          'Spend on labels before the credit is granted',
          deferred?.spendThreshold == null
            ? 'Not specified'
            : `${formatPrice(deferred.spendThreshold, { currency })} ${currency}`
        )}
        {renderRow(
          'Earning window',
          deferred?.earnWindowDays == null
            ? 'Not specified'
            : `${deferred.earnWindowDays} ${deferred.earnWindowDays === 1 ? 'day' : 'days'} after signup`
        )}
        {renderRow('Require a verified card', booleanLabel(deferred?.requireVerifiedCard))}
        {renderRow(
          'International labels do not count',
          booleanLabel(deferred?.excludeInternational)
        )}
      </Box>

      <Divider className={classes.divider} />

      <Typography className={classes.servicesTitle}>Services that do not count</Typography>
      {hasExcludedServices && deliveryServicesState.status === 'loading' && (
        <Typography className={classes.hint} role="status">
          Loading carrier service names...
        </Typography>
      )}
      {hasExcludedServices && deliveryServicesState.status === 'error' && (
        <Box>
          <Typography className={classes.hint} role="alert">
            {deliveryServicesState.error}. Saved providers and service codes are shown below.
          </Typography>
          <Button size="small" color="primary" onClick={() => setRetry((value) => value + 1)}>
            Retry
          </Button>
        </Box>
      )}
      {Array.isArray(excludedServices) && excludedServices.length ? (
        <Box className={classes.services}>
          <Table
            className={classes.table}
            size="small"
            stickyHeader
            aria-label="Excluded shipping services">
            <TableHead>
              <TableRow>
                <TableCell className={classes.cell}>Provider</TableCell>
                <TableCell className={classes.cell}>Service name</TableCell>
                <TableCell className={classes.cell}>Service code</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {excludedServices.map((service, index) => (
                <TableRow key={`${service.provider}:${service.serviceCode}:${index}`}>
                  <TableCell className={classes.cell}>
                    {service.provider || 'Not specified'}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {serviceNames.get(`${service.provider}|${service.serviceCode}`) ||
                      (deliveryServicesState.status === 'loading'
                        ? 'Loading...'
                        : 'Name unavailable')}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {service.serviceCode || 'Not specified'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Typography className={classes.hint}>
          {Array.isArray(excludedServices) ? 'No services excluded' : 'Not specified'}
        </Typography>
      )}
    </Dialog>
  );
};

export default DeferredCreditDetailsDialog;
